import { useCallback, useMemo } from 'react';
import type {
  ConnectStepsRequest,
  ConnectStepsResponse,
  DisconnectStepsRequest,
  DisconnectStepsResponse,
  SwimlaneColor,
  UpdateFlowPositionsRequest,
  UpdateFlowPositionsResponse
} from '@bliks-io/contracts';
import type { EdgeDescriptor, EdgeLayoutUpdate } from '@bliks-io/ui';
import type { EdgeAction, EdgeActionDeps } from '../edge-actions/types';
import { markTerminal, type TerminalStatus } from '../edge-actions/successorInvariant';
import {
  useConvertAssignmentToStep,
  useConvertStepToAssignment,
  useDeleteDraftAssignment,
  useMergeDraftNodes
} from '@/api/mutations/useProcessDraft';
import { useUpdateProcessLayout } from '@/api/mutations/useUpdateProcessLayout';
import { useDraftGesture } from '@/api/mutations/helpers/draftMutation';
import { toastApiError } from '@/api/mutations/toast-api-error';
import {
  useUnclearAssignmentActions,
  type ResolveUnclearWithAssigneeArgs
} from './useUnclearAssignmentActions';
import { useConnectionActions } from './useConnectionActions';
import { useEdgeAction } from './useEdgeAction';
import { useEdgeActionDeps } from './useEdgeActionDeps';
import { forkFromStart } from '../edge-actions/forkFromSource';
import { useStepActions } from './useStepActions';
import { useEventActions } from './useEventActions';
import { useNoteActions, type NoteActions } from './useNoteActions';

export interface UndoableEditorActions {
  addStep: (position: { x: number; y: number }, laneKey?: string) => Promise<void>;
  /**
   * Add a step connected from the start event (the empty-step placeholder's
   * action). `onCreated` gets the new step's stableStepId as soon as it is known.
   */
  addStepFromStart: (
    startEventId: string,
    position: { x: number; y: number },
    onCreated?: (stableStepId: string) => void
  ) => Promise<void>;
  deleteStep: (stepId: string) => Promise<void>;
  deleteSteps: (stepIds: string[]) => Promise<void>;
  /**
   * Add a first-class draft event. When `attachToStepId` is provided the new
   * event is also wired to that step's event input (shortcut for the
   * right-click-step menu entry).
   */
  addEvent: (position: { x: number; y: number }, attachToStepId?: string) => Promise<void>;
  /** Delete a draft event entity and strip referencing step inputs. */
  deleteEvent: (eventId: string) => Promise<void>;
  /**
   * Delete an event node by id: a legacy `event-<stepId>-<inputIdx>` node is
   * promoted to a first-class event server-side first.
   */
  deleteLegacyEvent: (nodeId: string) => Promise<void>;
  /** Delete a draft assignment (identified by its `stableStepId`). */
  deleteAssignment: (stableStepId: string) => Promise<void>;
  /** Move a manual draft step into the draft assignments collection. */
  convertStepToAssignment: (stableStepId: string) => Promise<void>;
  /** Move a draft assignment back into the manual draft steps collection. */
  convertAssignmentToStep: (stableStepId: string) => Promise<void>;
  /** Merge the `merged` card into the `survivor` card. */
  mergeNodes: (survivorStableStepId: string, mergedStableStepId: string) => Promise<void>;
  onConnectSteps: (dto: ConnectStepsRequest) => Promise<ConnectStepsResponse>;
  onDisconnectSteps: (dto: DisconnectStepsRequest) => Promise<DisconnectStepsResponse>;
  onSavePositions: (dto: UpdateFlowPositionsRequest) => Promise<UpdateFlowPositionsResponse>;
  onUpdateEdgeLayouts: (updates: EdgeLayoutUpdate[]) => Promise<void>;
  onEdgeAction: (action: EdgeAction, edge: EdgeDescriptor) => Promise<void>;
  /**
   * Flip a step into the requested terminal state (`NONE` end node or
   * `UNCLEAR` ambiguity marker), cascading the removal of any downstream
   * steps and draft assignments.
   */
  markStepAsTerminal: (stableStepId: string, status: TerminalStatus) => Promise<void>;
  /**
   * Flip every rule in `ruleIds` on the same decision step to the target
   * terminal status. Used by the gateway-handle context menu, where a single
   * output handle backs multiple rules with a shared `output` label.
   */
  markRulesAsTerminal: (
    stableStepId: string,
    ruleIds: readonly string[],
    status: TerminalStatus
  ) => Promise<void>;
  /**
   * Convert a regular draft assignment into an UNCLEAR end-node on its
   * predecessor step. Looks up the predecessor from the assignment's single
   * `previous_output` input and cascades into `markTerminal`.
   */
  markAssignmentUnclear: (stableStepId: string) => Promise<void>;
  /**
   * Resolve an UNCLEAR end-node by picking an assignee. Clears the predecessor
   * step's terminal status and materializes a fresh draft assignment wired to
   * the same rule branch (when `branchKey` resolves to a decision rule).
   */
  resolveUnclearWithAssignee: (args: ResolveUnclearWithAssigneeArgs) => Promise<void>;
  moveSwimlane: (
    laneKey: string,
    direction: 'up' | 'down',
    currentOrder: string[]
  ) => Promise<void>;
  /**
   * Set a lane's persisted base color (`null` clears back to the default).
   * `currentColors` is the full current mapping resolved by the caller — the
   * patch replaces the array wholesale.
   */
  setSwimlaneColor: (
    laneKey: string,
    color: string | null,
    currentColors: readonly SwimlaneColor[]
  ) => Promise<void>;
  /** Create a sticky note at a flow position (optionally pinned to nodes). */
  addNote: NoteActions['addNote'];
  /** Edit a note's text and/or category. */
  updateNote: NoteActions['updateNote'];
  /** Persist a note's geometry after a move or resize. */
  placeNote: NoteActions['placeNote'];
  /** Replace a note's target node set (link/unlink to nodes). */
  retargetNote: NoteActions['retargetNote'];
  /** Delete a sticky note. */
  deleteNote: NoteActions['deleteNote'];
}

interface UseUndoableEditorActionsArgs {
  processId: string;
  /**
   * Org-member / team lookup forwarded to `EdgeActionDeps` so the edge-action
   * lane resolver can map entity-ref `assignedTo` values back to a populated
   * department / team. Caller-provided so the editor's existing lookup query
   * results are reused instead of duplicating the fetch here.
   */
  getAssigneeLookup?: EdgeActionDeps['getAssigneeLookup'];
}

/**
 * Forward-only editor actions. Undo/redo is server-side, persistent
 * time-travel (ADR 0007) — each action just performs its draft mutation; the
 * draft-history capture on the backend records the entry, and the toolbar's
 * undo/redo (see `useDraftUndoControls`) steps the timeline. No client-side
 * inverse bookkeeping.
 */
export function useUndoableEditorActions({
  processId,
  getAssigneeLookup
}: UseUndoableEditorActionsArgs): UndoableEditorActions {
  const edgeActionDeps = useEdgeActionDeps({ processId, getAssigneeLookup });
  const { addStep, deleteSteps } = useStepActions({ processId });
  const { addNote, updateNote, placeNote, retargetNote, deleteNote } = useNoteActions({
    processId
  });
  const { onConnectSteps, onDisconnectSteps, onSavePositions, onUpdateEdgeLayouts } =
    useConnectionActions({ processId, edgeActionDeps });
  const onEdgeAction = useEdgeAction(edgeActionDeps);

  const addStepFromStart = useDraftGesture(
    processId,
    (
      startEventId: string,
      position: { x: number; y: number },
      onCreated?: (stableStepId: string) => void
    ) => forkFromStart(startEventId, position, edgeActionDeps, onCreated)
  );

  const convertStepToAssignmentMutation = useConvertStepToAssignment(processId);
  const convertAssignmentToStepMutation = useConvertAssignmentToStep(processId);
  const convertStepToAssignmentAsync = convertStepToAssignmentMutation.mutateAsync;
  const convertAssignmentToStepAsync = convertAssignmentToStepMutation.mutateAsync;

  const mergeNodesMutation = useMergeDraftNodes(processId);
  const mergeNodesAsync = mergeNodesMutation.mutateAsync;

  const deleteAssignmentMutation = useDeleteDraftAssignment(processId);
  const deleteAssignmentAsync = deleteAssignmentMutation.mutateAsync;

  const { addEvent, deleteEvent, deleteLegacyEvent } = useEventActions({
    processId,
    edgeActionDeps
  });

  const markStepAsTerminal = useDraftGesture(
    processId,
    (stableStepId: string, status: TerminalStatus) =>
      markTerminal(stableStepId, undefined, { status }, edgeActionDeps)
  );

  const markRulesAsTerminal = useDraftGesture(
    processId,
    (stableStepId: string, ruleIds: readonly string[], status: TerminalStatus) =>
      markTerminal(stableStepId, ruleIds, { status }, edgeActionDeps)
  );

  const { markAssignmentUnclear, resolveUnclearWithAssignee } = useUnclearAssignmentActions(
    processId,
    edgeActionDeps
  );

  const updateLayoutMutation = useUpdateProcessLayout(processId, 'draft');
  const updateLayoutAsync = updateLayoutMutation.mutateAsync;

  // Flip a manual step → assignment. The backend stashes the step's task
  // content server-side, so the inverse convert (via undo) restores it.
  const convertStepToAssignment = useCallback(
    async (stableStepId: string) => {
      const draftStepId = edgeActionDeps
        .getCachedSteps()
        .find((s) => String(s.stableStepId) === stableStepId)?._id;
      if (!draftStepId) return;
      try {
        await convertStepToAssignmentAsync({ stepId: draftStepId });
      } catch (error) {
        toastApiError(error, 'errors.unknown');
      }
    },
    [convertStepToAssignmentAsync, edgeActionDeps]
  );

  const convertAssignmentToStep = useCallback(
    async (stableStepId: string) => {
      try {
        await convertAssignmentToStepAsync({ stableStepId });
      } catch (error) {
        toastApiError(error, 'errors.unknown');
      }
    },
    [convertAssignmentToStepAsync]
  );

  const deleteAssignment = useCallback(
    async (stableStepId: string) => {
      await deleteAssignmentAsync(stableStepId);
    },
    [deleteAssignmentAsync]
  );

  const mergeNodes = useCallback(
    async (survivorStableStepId: string, mergedStableStepId: string) => {
      await mergeNodesAsync({ survivorStableStepId, mergedStableStepId });
    },
    [mergeNodesAsync]
  );

  /**
   * Reorder swimlanes. The builder closes over the full effective lane order
   * at build time and hands it to the move callbacks, so this action always
   * receives a complete ordering — even when `draft.swimlaneOrder` is empty
   * (default sort) or out-of-sync with newly-introduced lanes.
   */
  const moveSwimlane = useCallback(
    async (laneKey: string, direction: 'up' | 'down', currentOrder: string[]) => {
      const idx = currentOrder.indexOf(laneKey);
      if (idx === -1) return;
      const swapWith = direction === 'up' ? idx - 1 : idx + 1;
      if (swapWith < 0 || swapWith >= currentOrder.length) return;

      const next = [...currentOrder];
      [next[idx], next[swapWith]] = [next[swapWith], next[idx]];

      await updateLayoutAsync({ swimlaneOrder: next });
    },
    [updateLayoutAsync]
  );

  // No client-side prev-color bookkeeping — the server-side draft history
  // (ADR 0007) restores the previous laneColors on undo.
  const setSwimlaneColor = useCallback(
    async (laneKey: string, color: string | null, currentColors: readonly SwimlaneColor[]) => {
      const next = currentColors.filter((entry) => entry.laneKey !== laneKey);
      if (color !== null) next.push({ laneKey, color });
      await updateLayoutAsync({ laneColors: next });
    },
    [updateLayoutAsync]
  );

  return useMemo(
    () => ({
      addStep,
      addStepFromStart,
      deleteStep: (stepId: string) => deleteSteps([stepId]),
      deleteSteps,
      addEvent,
      deleteEvent,
      deleteLegacyEvent,
      deleteAssignment,
      convertStepToAssignment,
      convertAssignmentToStep,
      mergeNodes,
      onConnectSteps,
      onDisconnectSteps,
      onSavePositions,
      onUpdateEdgeLayouts,
      onEdgeAction,
      markStepAsTerminal,
      markRulesAsTerminal,
      markAssignmentUnclear,
      resolveUnclearWithAssignee,
      moveSwimlane,
      setSwimlaneColor,
      addNote,
      updateNote,
      placeNote,
      retargetNote,
      deleteNote
    }),
    [
      addStep,
      addStepFromStart,
      deleteSteps,
      addEvent,
      deleteEvent,
      deleteLegacyEvent,
      deleteAssignment,
      convertStepToAssignment,
      convertAssignmentToStep,
      mergeNodes,
      onConnectSteps,
      onDisconnectSteps,
      onSavePositions,
      onUpdateEdgeLayouts,
      onEdgeAction,
      markStepAsTerminal,
      markRulesAsTerminal,
      markAssignmentUnclear,
      resolveUnclearWithAssignee,
      moveSwimlane,
      setSwimlaneColor,
      addNote,
      updateNote,
      placeNote,
      retargetNote,
      deleteNote
    ]
  );
}
